import { all, take, fork, call } from 'redux-saga/effects';
import { matchPath } from 'react-router-dom';
import { Map } from 'immutable';
import {
  mountTable,
  unmountTable,
  mountForm,
  unmountForm,
} from '@kineticdata/react';
import { regSaga } from './saga';
import { dispatch } from './store';

let routes = {};
let pendingRoutes = {};
let previous = Map();
let previousPathname = null;

export const regRoute = (name, spec, config = {}) => {
  pendingRoutes[name] = {
    spec: typeof spec === 'string' ? { path: spec } : spec,
    config,
  };
};

const ROUTE_MATCH = /(ENTER|LEAVE)_(.+)/;

regSaga('WATCH_ROUTES', function* () {
  while (true) {
    try {
      const action = yield take('*');
      const { type } = action;

      // This may be a route we're interested in.
      const result = type.match(ROUTE_MATCH);
      if (result) {
        // eslint-disable-next-line no-unused-vars
        const [_, routeAction, routeName] = result;
        const route = routes[routeName];

        if (!route) {
          continue;
        }

        // Handle Enter Tasks.
        if (route && route.config.enterTask && routeAction === 'ENTER') {
          yield fork(route.config.enterTask, action);
        }
        // Handle Leave Tasks.
        if (route && route.config.leaveTask && routeAction === 'LEAVE') {
          yield fork(route.config.leaveTask, action);
        }
        // Handle Table Mounts
        if (route && route.config.tableKey) {
          const tableKey =
            typeof route.config.tableKey === 'function'
              ? route.config.tableKey(action)
              : route.config.tableKey;

          if (routeAction === 'ENTER') {
            if (tableKey instanceof Array) {
              yield all(tableKey.map(tk => call(mountTable, tk)));
            } else {
              yield call(mountTable, tableKey);
            }
          } else if (routeAction === 'LEAVE') {
            if (tableKey instanceof Array) {
              yield all(tableKey.map(tk => call(unmountTable, tk)));
            } else {
              yield call(unmountTable, tableKey);
            }
          }
        }

        // Handle Form Mounts
        if (route && route.config.formKey) {
          const formKey =
            typeof route.config.formKey === 'function'
              ? route.config.formKey(action)
              : route.config.formKey;

          if (routeAction === 'ENTER') {
            if (formKey instanceof Array) {
              yield all(formKey.map(tk => call(mountForm, tk)));
            } else {
              yield call(mountForm, formKey);
            }
          } else if (routeAction === 'LEAVE') {
            if (formKey instanceof Array) {
              yield all(formKey.map(tk => call(unmountForm, tk)));
            } else {
              yield call(unmountForm, formKey);
            }
          }
        }
      }
    } catch (e) {
      console.warn('There was a problem while handing route tasks.');
    }
  }
});

export const commitRoutes = location => {
  routes = { ...routes, ...pendingRoutes };
  pendingRoutes = {};
  checkRoutes(location);
};

export const checkRoutes = location => {
  const current = Map(routes)
    .map(route =>
      matchPath(
        typeof route.spec === 'string' ? { path: route.spec } : route.spec,
        typeof route.spec.pathnameTransform === 'function'
          ? route.spec.pathnameTransform(location.pathname)
          : location.pathname,
      ),
    )
    .filter(matchData => matchData);

  previous
    .filter(
      (matchData, name) =>
        !current.has(name) ||
        current.getIn([name, 'pathnameBase']) !== matchData.pathnameBase,
    )
    .forEach((matchData, name) => dispatch(`LEAVE_${name}`, matchData));

  if (previousPathname !== location.pathname) {
    dispatch('LOCATION_CHANGE', current.keySeq().toSet());
  }

  current
    .filter(
      (matchData, name) =>
        !previous.has(name) ||
        previous.getIn([name, 'pathnameBase']) !== matchData.pathnameBase,
    )
    .forEach((matchData, name) => dispatch(`ENTER_${name}`, matchData));

  previous = current;
  previousPathname = location.pathname;
};
